// Listing modes for Onboarding — Checklist / SOPs / Action Register /
// Reports & Forms. Aaron 2026-10-16 on g2 /app/onboarding: "move these tabs to
// mode switchers as per standard module patterns".
//
// Until then the four were a <TabBar> held in useState over the list, which
// PATTERNS.md BP-14 forbids ("Never use inner tabs for listing modes"). They
// are ROUTES now, under OnboardingLayout's breadcrumb switcher, and registered
// in app/navModeRegistry.ts. Mirrored from src/maintenance-log/modes.ts
// (itself from src/blank/BlankLayout.tsx, Rule 0).
//
//   <base>/checklist        → OnboardingList          (the phase-grouped task grid)
//   <base>/sops             → SopsList                (every SOP on the tenant's Hub)
//   <base>/action-register  → the Action Register module, /app/actionregister   (LINKED mode)
//   <base>/reports-forms    → the Reports & Forms module, /app/reports-and-forms (LINKED mode)
//
// The two LINKED modes are other modules: the switcher entry goes to the module's
// own route, as its nav menu link does (BP-116; Aaron 2026-10-18, "action menu and
// reports and forms should just navigate to the module"). Until then they iframed
// those pages under this layout (OnboardingEmbed, removed): two nav bars, two headers.
//
// SOPs was "Process Map" (segment process-map): first a launcher to
// /app/help-v2/aeronet-mro-process-map, then (2026-10-16) a port of the legacy User
// Journeys register. Aaron 2026-10-18: "should be called "sops". should be a
// standard data grid with all the SOPs. and the stake holders and status. - with a
// link to all outputs". The journeys that had a Hub page are SOP pages in the new
// list; the rest only linked files.aeronet.nz. The old segment is a renamed mode
// (components/renamedModeSegments.tsx): saved URLs and a stored mode land on sops.
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";
import { isInModuleMode, type LinkedModes } from "../components/linkedModeSegments";
import { migrateModeSegment } from "../components/renamedModeSegments";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table. The labels are the
 * tab labels they replace.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  checklist: "Checklist",
  sops: "SOPs",
  "action-register": "Action Register",
  "reports-forms": "Reports & Forms",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85). Each read off what the mode renders.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  checklist: "Go-live tasks by phase",
  sops: "Every SOP, owner, status",
  "action-register": "Every action raised",
  "reports-forms": "Stationery templates",
};

/**
 * Modes that are other modules (BP-116): segment → that module's ROUTER path.
 * Both are static routes in App.tsx (`actionregister/*`; `reports-and-forms/*`
 * behind the `module_stationery` feature, which Features.tsx registers active),
 * so they exist on every tenant this module mounts on, and they are the paths
 * the nav menu links use (App.tsx setUpNav: `actionregister` keeps its function
 * name, `module_stationery` → "reports-and-forms").
 */
export const LINKED_MODES: LinkedModes = {
  "action-register": "/actionregister",
  "reports-forms": "/reports-and-forms",
};

/** Checklist: it was the first tab, and what a bare /onboarding showed. */
export const DEFAULT_MODE_SEGMENT = "checklist";

/**
 * `<module>.viewMode`. New: the tab was never persisted, so there is no stored
 * value to migrate.
 */
export const MODE_STORAGE_KEY = "onboarding.viewMode";

/**
 * Is this a real mode segment? hasOwnProperty, NOT `in` — `in` walks the
 * prototype chain, so "constructor" would test true and be persisted
 * (regressions f74a205b / 2deb1dee).
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/** Redirect target for a bare visit to the module root. */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    // A renamed mode reads as its new name (a stored "process-map" opens sops).
    stored = migrateModeSegment(window.localStorage.getItem(MODE_STORAGE_KEY));
  } catch {
    return DEFAULT_MODE_SEGMENT;
  }
  // A linked mode is another module: restoring it would bounce a bare visit out of Onboarding.
  return stored && isModeSegment(stored) && isInModuleMode(stored, LINKED_MODES) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. The base is
 * derived from the LIVE pathname, not hardcoded: a nav-mounted module can be
 * reached at more than one alias.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: "/" + "" would make the mode links
  // "//checklist", a protocol-relative URL that leaves the site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
